/**
 * 統一的 API 調用服務
 * 封裝所有後端 API 調用，自動處理 token 獲取和刷新，統一錯誤處理
 */

const API_URL = process.env.BACKEND_URL || '';

/**
 * 檢查 token 是否過期
 * @param {string} token - JWT token
 * @returns {boolean} - true 表示過期或無效
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    while (base64.length % 4) {
      base64 += '=';
    }

    const payload = JSON.parse(atob(base64));
    const exp = payload.exp;
    const now = Math.floor(Date.now() / 1000);

    return exp <= (now + 300); // 提前 5 分鐘視為過期
  } catch (error) {
    console.error("Error parsing token:", error);
    return true;
  }
};

/**
 * 通用的 API 調用函數
 * @param {string} url - API 端點 URL
 * @param {Object} options - fetch 選項
 * @param {Function} getAccessToken - 獲取 token 的函數
 * @param {Object} tokenRef - token 的 ref 對象（用於緩存和清除）
 * @returns {Promise<Response>} - fetch Response 對象
 */
export const apiCall = async (url, options = {}, getAccessToken, tokenRef = { current: null }) => {
  const makeRequest = async (token) => {
    // 確保不會重複添加 Authorization header
    const headers = { ...options.headers };
    delete headers.Authorization;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  };

  let token = await getAccessToken();
  if (!token) {
    throw new Error("無法獲取 token");
  }

  let response = await makeRequest(token);

  // 如果收到 401，嘗試刷新 token 並重試一次
  if (response.status === 401) {
    console.log("Token 已過期，嘗試刷新");
    tokenRef.current = null; // 清除過期 token
    token = await getAccessToken({ refresh: true });
    
    if (token) {
      // 使用新 token 重試
      response = await makeRequest(token);
    } else {
      throw new Error("無法刷新 token");
    }
  }
  
  return response;
};

/**
 * 構建完整的 API URL
 * @param {string} endpoint - API 端點路徑
 * @returns {string} - 完整的 API URL
 */
export const buildApiUrl = (endpoint) => {
  // 移除開頭的斜線（如果有的話）
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  // 確保 API_URL 以斜線結尾（如果有的話，移除）
  const cleanBaseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
  return cleanBaseUrl ? `${cleanBaseUrl}/${cleanEndpoint}` : `/${cleanEndpoint}`;
};

